// Module-level timezone — set once at app startup via setAppTimezone()
let _tz = "America/New_York";

export function setAppTimezone(tz) {
  if (tz && typeof tz === "string") _tz = tz;
}

export function getAppTimezone() {
  return _tz;
}

// Naive timestamp regex: "YYYY-MM-DDTHH:MM:SS" or "YYYY-MM-DD HH:MM:SS",
// optionally with fractional seconds — i.e. NO trailing Z and NO ±HH:MM offset.
// ShipHero's GraphQL API returns timestamps in this naive shape and they
// actually represent UTC; without a marker, browsers treat them as local
// time, which shifts display by the browser's offset (e.g. 5h30m off in
// IST). Append "Z" so they're parsed as UTC and then converted to ET.
const NAIVE_TS_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

function normalizeDate(value) {
  if (!value) return null;
  let v = value;
  if (typeof v === "string" && NAIVE_TS_RE.test(v)) {
    v = v.replace(" ", "T") + "Z";
  }
  const date = v instanceof Date ? v : new Date(v);
  return Number.isFinite(date.getTime()) ? date : null;
}

export function formatDateTime(value, options = {}) {
  const date = normalizeDate(value);
  if (!date) return "\u2014";
  return date.toLocaleString("en-US", {
    timeZone: _tz,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...options,
  });
}

export function formatDate(value, options = {}) {
  const date = normalizeDate(value);
  if (!date) return "\u2014";
  return date.toLocaleDateString("en-US", {
    timeZone: _tz,
    month: "short",
    day: "numeric",
    year: "numeric",
    ...options,
  });
}

export function formatTime(value, options = {}) {
  const date = normalizeDate(value);
  if (!date) return "\u2014";
  return date.toLocaleTimeString("en-US", {
    timeZone: _tz,
    hour: "2-digit",
    minute: "2-digit",
    ...options,
  });
}

export function getTimezoneLabel() {
  // Short label like "ET", "CT", "UTC" etc.
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: _tz,
      timeZoneName: "short",
    }).formatToParts(new Date());
    return parts.find(p => p.type === "timeZoneName")?.value || _tz;
  } catch {
    return _tz;
  }
}

// Legacy aliases for backward compat
export const formatEasternDateTime = formatDateTime;
export const formatEasternDate = formatDate;
export const formatEasternTime = formatTime;
export const getEasternTimeZoneLabel = getTimezoneLabel;
